import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Code, Palette, Film, Camera, Users, Zap } from 'lucide-react';
import Marquee from 'react-fast-marquee';

const HomePage = () => {
  const services = [
    { icon: <Code />, title: 'App & Web Development', desc: 'Scalable iOS & Android apps and Fast, modern websites built for real-world performance' },
  { icon: <Palette />, title: 'Digital Marketing', desc: 'Performance-driven campaigns that convert attention into revenue' },
  { icon: <Film />, title: 'AI Cinematic Content', desc: 'TVC ads & brand films crafted entirely with AI' },
  { icon: <Camera />, title: 'AI Product Shoots', desc: 'Hyperrealistic product visuals — no studio required' },
  { icon: <Users />, title: 'AI Character Design', desc: 'Custom mascots & brand heroes that audiences remember' },
    { icon: <Zap />, title: 'Brand Films', desc: 'End-to-end AI storytelling' },
  ];

  const featuredWork = [
    {
      image: 'https://firebasestorage.googleapis.com/v0/b/ai-search-e7e28.firebasestorage.app/o/Appdroid%20Website%20Data%2F3%20dim.webp?alt=media&token=01c57d78-d1cd-47f7-a6c4-7aab80fd6561',
      title: 'Luxury Jewellery Collection',
      category: 'AI Jewellery Shoot',
    },
    {
      image: 'https://firebasestorage.googleapis.com/v0/b/ai-search-e7e28.firebasestorage.app/o/Appdroid%20Website%20Data%2FScreenshot%202026-04-11%20at%202.14.36%E2%80%AFPM.webp?alt=media&token=4695f830-c849-4d8c-89a3-5e6f3ec72eb9',
      title: 'Cinematic Luxury',
      category: 'AI Cinematic Shoot',
    },
    {
      image: 'https://firebasestorage.googleapis.com/v0/b/ai-search-e7e28.firebasestorage.app/o/Appdroid%20Website%20Data%2F04-9_revised%20.webp?alt=media&token=f51cb9f3-c3e3-4d92-adad-582b7f11de0a',
      title: 'Fashion Editorial',
      category: 'AI Model Shoot',
    },
  ];

  const clients = [
    'Saregama Ltd.',
    'Chandukaka Saraf Jewels',
    'Mangal Graha Mandir',
    'Jain Farm Fresh Foods',
    'Indosource Technologies',
    'Hello Media Group',
    'Indian Red Cross Society, Jalgaon',
    'Nobel Foundation',
  ];

  const marqueeItems = [
  'App Development', 'AI Cinematic Videos', 'Brand Films', 'Digital Marketing',
  'TVC Ads', 'AI Product Shoots', 'AI Character Design', 'Web Development',
  'UGC Content', 'AI Model Shoots', 'Software Solutions', 'AI Studio',
];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.6)' }}
          >
            <source
              src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4"
              type="video/mp4"
            />
          </video>
          {/* Stronger gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                'radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.2) 0%, rgba(10, 10, 10, 0) 70%)',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
          >
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-black tracking-tighter leading-none uppercase mb-6 text-white drop-shadow-2xl">
              We Build.
              <br />
              <span className="text-purple-400">We Generate.</span>
              <br />
              We Dominate.
            </h1>
            <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto mb-12 leading-relaxed drop-shadow-lg">
              One studio. Every capability. We merge software engineering
  with AI-powered content creation, so your brand grows from the inside out.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/work"
                data-testid="view-work-btn"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-full font-bold tracking-wide transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(124,58,237,0.5)] inline-flex items-center justify-center gap-2"
              >
                View Our Work <ArrowRight size={20} />
              </Link>
              <Link
                to="/contact"
                data-testid="lets-talk-btn"
                className="border-2 border-white/80 hover:border-white text-white px-8 py-4 rounded-full font-bold tracking-wide transition-all hover:bg-white/10 inline-flex items-center justify-center"
              >
                Let's Talk
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee Strip */}
      <section className="border-y border-themed py-6 bg-card">
        <Marquee gradient={false} speed={50}>
          {marqueeItems.map((item, index) => (
            <span key={index} className="mx-8 text-lg font-medium">
              {item} <span className="text-purple-500 mx-8">•</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* About Teaser */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-card border border-themed rounded-2xl p-8">
                <div className="text-5xl font-black text-purple-500 mb-2">7+</div>
                <div className="text-sm text-muted">Years</div>
              </div>
              <div className="bg-card border border-themed rounded-2xl p-8">
                <div className="text-5xl font-black text-purple-500 mb-2">50+</div>
                <div className="text-sm text-muted">Clients</div>
              </div>
              <div className="bg-card border border-themed rounded-2xl p-8">
                <div className="text-5xl font-black text-purple-500 mb-2">Many</div>
                <div className="text-sm text-muted">Possibilities</div>
              </div>
              <div className="bg-card border border-themed rounded-2xl p-8">
                <div className="text-5xl font-black text-purple-500 mb-2">100%</div>
                <div className="text-sm text-muted">AI-Generated</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6">
                One Studio.
                <br />
                <span className="text-purple-500">All Capabilities.</span>
              </h2>
              <p className="text-lg text-muted leading-relaxed">
                Appdroid AI is a full-spectrum intelligent studio. We build the apps and platforms your
  business runs on, and create the AI-powered content that makes your brand unforgettable.
  From code to cinema, under one roof.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 md:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-4">
              What We Do
            </h2>
            <p className="text-lg text-muted">From code to cinema. From strategy to scale.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="bg-card border border-themed rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-500 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <div className="text-purple-500 mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <p className="text-sm text-muted">{service.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="text-purple-500 font-bold inline-flex items-center gap-2 hover:gap-4 transition-all"
            >
              View All Services <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-4">
              Featured Work
            </h2>
            <p className="text-lg text-muted">Projects that push boundaries.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredWork.map((work, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl mb-4 aspect-[4/5]">
                  <img
                    src={work.image}
                    alt={work.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                    <div className="flex items-center gap-2 text-white">
                      <span className="font-bold">View Project</span>
                      <ArrowRight size={20} />
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <h3 className="text-xl font-bold">{work.title}</h3>
                  <p className="text-sm text-purple-500">{work.category}</p>
                  <p className="text-sm text-muted">{work.client}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/work"
              data-testid="view-all-work-btn"
              className="text-purple-500 font-bold inline-flex items-center gap-2 hover:gap-4 transition-all"
            >
              View All Work <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Clients Strip */}
      <section className="py-24 md:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-4">
              Brands That Trust Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {clients.map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-card border border-themed rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300 flex items-center justify-center text-center min-h-[150px]"
              >
                <span className="text-lg font-bold">{client}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-600/20 to-purple-900/20 border border-purple-500/30 rounded-3xl p-12 md:p-20 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight mb-6">
                Ready to Create Something
                <br />
                <span className="text-purple-400">Extraordinary?</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8">Let's build the future together.</p>
              <Link
                to="/contact"
                data-testid="start-project-btn"
                className="bg-purple-600 hover:bg-purple-700 text-white px-10 py-5 rounded-full font-bold text-lg tracking-wide transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(124,58,237,0.6)] inline-flex items-center gap-2"
              >
                Start a Project <Sparkles size={24} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;